/**
 * Shared per-route SEO metadata.
 *
 * Consumed by:
 *   - <SeoHead /> (react-helmet-async) for client-side navigation updates.
 *   - scripts/postbuild-seo.js for build-time static HTML shells.
 *
 * KEEP THIS FILE FRAMEWORK-FREE — the postbuild script requires it via Node.
 */
const SITE_URL = "https://fijterminal.com";
const SITE_NAME = "FIJ Terminal";
const OG_IMAGE = `${SITE_URL}/og-fij-terminal.png`;

/** Homepage — the primary brand landing page. Strongest SEO signal. */
const HOME_META = {
  path: "/",
  canonical: `${SITE_URL}/`,
  title: "FIJ Terminal | AI-Powered Stock Analysis & Daily Market Intelligence",
  description:
    "FIJ Terminal — the AI-powered Indian stock market platform. Live Nifty 50, Sensex, Bank Nifty and Midcap indices, a daily pre-market Morning Brief, a live Financial Journal of market-moving news, and AI-generated verdicts for any NSE stock. Bloomberg-style dark terminal for retail and pro traders.",
  h1: "FIJ Terminal",
  intro:
    "FIJ Terminal is an AI-powered Indian stock market intelligence platform. Live Nifty 50, Sensex, Bank Nifty, Midcap and Smallcap indices with terminal-style charts, an AI-generated pre-market Morning Brief, a Financial Journal of market-moving news, and an AI verdict for any NSE-listed stock — everything a modern Indian investor needs on one dark, low-latency screen.",
  contentPreview: [
    "Live NSE and BSE index quotes with 1D / 1W / 1M / 3M / YTD terminal-style charts.",
    "Daily AI Morning Brief covering US closing, global cues, FII/DII flows, and India-specific events at 07:00 IST.",
    "Financial Journal streaming market-moving headlines from MarketAux, Finnhub and Alpha Vantage with sentiment-tagged AI analysis.",
    "AI Stock Analysis with verdict, confidence, strengths, risks and latest company news for any NSE-listed stock.",
  ],
  schemaType: "WebPage",
};

const DASHBOARD_META = {
  path: "/dashboard",
  canonical: `${SITE_URL}/dashboard`,
  title: "Dashboard | Live Nifty 50, Sensex & Indian Indices — FIJ Terminal",
  description:
    "Live Indian market dashboard — Nifty 50, Sensex, Bank Nifty, Nifty Midcap 100 and Smallcap 100 with previous-close referenced intraday charts, today's high/low, 52-week ranges and global cues (S&P 500, Dow, Nikkei, Gold, Silver, USD/INR).",
  h1: "Indian Market Dashboard — Nifty 50 · Sensex · Bank Nifty",
  intro:
    "The FIJ Terminal Dashboard is your single-screen command centre for the Indian equity market. Every headline index, global cross-asset indicator and today's market-moving news is streamed live with terminal-grade dark charts.",
  contentPreview: [
    "Market Snapshot: switch between Nifty 50, Sensex, Nifty Midcap 100 and Nifty Smallcap 100 with 1D · 1W · 1M · 3M · YTD terminal charts and a dashed previous-close reference on every timeframe.",
    "Market Statistics: today's high, today's low, 52-week high, 52-week low and previous close for the selected index.",
    "Global Indicators: S&P 500, Dow Jones, Nikkei 225, USD/INR, Gold and Silver refreshed every 60 seconds.",
    "Recent Events: the newest impactful market headlines with severity labels, deep-linked to full AI event pages.",
  ],
  schemaType: "WebPage",
};

const MORNING_BRIEF_META = {
  path: "/morning-brief",
  canonical: `${SITE_URL}/morning-brief`,
  title: "Morning Brief | Pre-Market Intelligence for Nifty & Sensex — FIJ Terminal",
  description:
    "Daily Indian stock market morning briefing at 07:00 IST — Nifty & Sensex outlook, FII/DII flows, US closing summary, global cues (Dow, Nasdaq, S&P 500, Nikkei), crude oil, gold and macro insights. Written by Claude Sonnet AI.",
  h1: "Morning Brief — Pre-Market Intelligence for the Indian Market",
  intro:
    "Every trading day at 07:00 IST the FIJ Terminal Morning Brief distils the overnight session — US close, global cues, oil, gold, USD/INR, FII/DII activity and India-specific catalysts — into an institutional-grade pre-market read.",
  contentPreview: [
    "US Recap: Dow, Nasdaq and S&P 500 closing action, earnings pulse and Fed commentary.",
    "Global Cues: Europe, Nikkei, Hang Seng, DXY, US 10-year yields, crude oil and gold.",
    "India Setup: SGX Nifty / GIFT Nifty indication, INR range, FII / DII flows, sectors in focus, corporate earnings and economic calendar.",
    "AI-generated with Claude Sonnet on live data — refreshed daily at 07:00 IST and cached for the entire session.",
  ],
  schemaType: "Article",
};

const FINANCIAL_JOURNAL_META = {
  path: "/financial-journal",
  canonical: `${SITE_URL}/financial-journal`,
  title: "Financial Journal | Live Indian & Global Market News — FIJ Terminal",
  description:
    "Live streaming feed of Indian and global financial news with AI-tagged sentiment, sector impact and event severity. Covers RBI, SEBI, NSE, BSE, corporate earnings, oil, bond yields and geopolitics.",
  h1: "Financial Journal — Live Market News & AI Event Analysis",
  intro:
    "The Financial Journal is a live-updating stream of headlines that move Indian and global markets. Every item is tagged with sentiment, sector impact, severity and, where applicable, an AI-generated event breakdown you can drill into.",
  contentPreview: [
    "Filter by category — RBI, SEBI, NSE, BSE, Government Policy, Corporate Earnings, US Markets, Oil, Currencies, Bond Yields, Geopolitics.",
    "Filter by severity — high / medium / low.",
    "Multi-provider fallback across MarketAux, Finnhub and Alpha Vantage keeps the feed live even if one source is rate-limited.",
    "Click any headline to open a full AI event page with affected sectors, impact duration, related stocks and similar historical events.",
  ],
  schemaType: "WebPage",
};

const STOCK_ANALYSIS_META = {
  path: "/stock-analysis",
  canonical: `${SITE_URL}/stock-analysis`,
  title: "Stock Analysis | AI Verdict for NSE Stocks — FIJ Terminal",
  description:
    "AI-powered analysis for any NSE-listed stock — company snapshot, sector, market cap, P/E, 52-week range, terminal-style price chart and an AI verdict with confidence, strengths, risks and latest company news.",
  h1: "AI Stock Analysis — NSE Company Snapshot & Verdict",
  intro:
    "Search any NSE-listed stock to instantly see its live snapshot (price, sector, market cap, P/E, 52-week range) alongside a terminal-style price chart and an AI-generated Investment Verdict with confidence score, strengths and risks.",
  contentPreview: [
    "Live snapshot: current price, today's change, market cap, sector, industry, 52-week high/low, P/E ratio.",
    "Terminal chart: 1D · 1W · 1M · 3M · YTD · 1Y with dashed previous-close reference on every timeframe.",
    "AI Summary: concise thesis on the stock powered by Claude Sonnet.",
    "Investment Verdict: BUY / HOLD / SELL bias with confidence percentage, plus a bulleted list of strengths and risks and the latest relevant news.",
  ],
  schemaType: "WebPage",
};

/**
 * Legal pages — noindex,follow. We don't want Google to rank the Privacy
 * Policy / Terms / Contact pages ahead of the FIJ Terminal brand pages,
 * but we still want link equity to flow back to the homepage.
 */
const PRIVACY_META = {
  path: "/privacy",
  canonical: `${SITE_URL}/privacy`,
  title: "Privacy Policy — FIJ Terminal",
  description: "Privacy Policy for FIJ Terminal — how we handle your data.",
  h1: "Privacy Policy",
  intro: "This page describes how FIJ Terminal collects, uses and protects user data.",
  contentPreview: [],
  schemaType: "WebPage",
  noindex: true,
};

const TERMS_META = {
  path: "/terms",
  canonical: `${SITE_URL}/terms`,
  title: "Terms & Conditions — FIJ Terminal",
  description: "Terms of Service and disclaimers for using FIJ Terminal.",
  h1: "Terms & Conditions",
  intro: "Terms that govern your use of FIJ Terminal, including disclaimers about market data and AI-generated content.",
  contentPreview: [],
  schemaType: "WebPage",
  noindex: true,
};

const CONTACT_META = {
  path: "/contact",
  canonical: `${SITE_URL}/contact`,
  title: "Contact — FIJ Terminal",
  description: "Get in touch with the FIJ Terminal team.",
  h1: "Contact",
  intro: "Reach out to the FIJ Terminal team with questions, feedback or partnership enquiries.",
  contentPreview: [],
  schemaType: "WebPage",
  noindex: true,
};

export const ROUTE_META = {
  "/": HOME_META,
  "/dashboard": DASHBOARD_META,
  "/morning-brief": MORNING_BRIEF_META,
  "/financial-journal": FINANCIAL_JOURNAL_META,
  "/stock-analysis": STOCK_ANALYSIS_META,
  "/privacy": PRIVACY_META,
  "/terms": TERMS_META,
  "/contact": CONTACT_META,
};

/** Legacy route → SEO route mapping. Legacy paths still work; canonicals point to SEO paths. */
export const LEGACY_ALIAS = {
  "/events": "/financial-journal",
  "/stocks": "/stock-analysis",
};

export const SITE = { SITE_URL, SITE_NAME, OG_IMAGE };

/** Return metadata for a given pathname (falls back to HOME_META). */
export function getRouteMeta(pathname) {
  if (!pathname) return HOME_META;
  const canonicalPath = LEGACY_ALIAS[pathname] || pathname;
  return ROUTE_META[canonicalPath] || HOME_META;
}
